import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import Shell from "@/layout/Shell";
import { useFetch } from "@/lib/useFetch";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { Building2, CheckCircle2, AlertCircle, Download, ChevronRight } from "lucide-react";

const HALL_COLORS = ["#0038C8","#10B981","#8B5CF6","#F59E0B","#EC4899","#06B6D4"];
const TIME_SLOTS  = [
  "8:00 AM – 11:00 AM","9:00 AM – 12:00 PM","10:00 AM – 1:00 PM",
  "11:00 AM – 2:00 PM","1:00 PM – 4:00 PM","2:00 PM – 5:00 PM",
  "3:00 PM – 6:00 PM","9:00 AM – 5:00 PM","Full Day",
];

// Time-overlap helpers (must match the Worker's slotsConflict logic) so the
// dropdown can grey out slots already taken for the selected hall + date.
function timeToMinutes(t) {
  const m = String(t).trim().toUpperCase().match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/);
  if (!m) return null;
  let h = parseInt(m[1], 10); const min = m[2] ? parseInt(m[2], 10) : 0; const ap = m[3];
  if (ap === "PM" && h !== 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return h * 60 + min;
}
function slotToRange(s) {
  s = String(s || "").trim();
  const lower = s.toLowerCase();
  if (lower === "full day" || lower === "full-day") return { start: 0, end: 1440, full: true };
  const parts = s.replace(/[\u2013\u2014]/g, "-").split("-");
  if (parts.length !== 2) return null;
  const a = timeToMinutes(parts[0]), b = timeToMinutes(parts[1]);
  if (a == null || b == null || b <= a) return null;
  return { start: a, end: b, full: false };
}
function slotsOverlap(a, b) {
  if (a === b) return true;
  const ra = slotToRange(a), rb = slotToRange(b);
  if (!ra || !rb) return a === b;
  if (ra.full || rb.full) return true;
  return ra.start < rb.end && rb.start < ra.end;
}

function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload  = e => res(e.target.result.split(",")[1]);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function BookHall() {
  const nav   = useNavigate();
  const halls = useFetch(() => api.listHalls().then(r => r?.halls || []), []);
  const letterRef = useRef();

  const [selected,  setSelected]  = useState(null);
  const [mode,      setMode]      = useState("single"); // single | multi
  const [avail,     setAvail]     = useState(null);
  const [checking,  setChecking]  = useState(false);
  const [submitting,setSubmitting]= useState(false);
  const [result,    setResult]    = useState(null);
  const [multiPreview, setMultiPreview] = useState(null);
  const [daySlots, setDaySlots] = useState([]); // slots already booked for selected hall+date

  const [form, setForm] = useState({
    date: "", endDate: "", timeSlot: "", customSlot: "",
    eventName: "", deptClub: "", name: "", phone: "", email: "",
    specialReqs: "", bookingType: "single",
  });
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  // Load already-booked slots for the selected hall on the chosen date, so the
  // slot dropdown can grey out anything that overlaps an existing booking.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selected || !form.date || mode === "multi" || (selected.status || "active") !== "active") { setDaySlots([]); return; }
      try {
        const r = await api.eventsByDate(form.date);
        const mine = (r?.events || [])
          .filter(e => String(e.hallId) === String(selected.id))
          .map(e => String(e.timeSlot));
        if (!cancelled) setDaySlots(mine);
      } catch { if (!cancelled) setDaySlots([]); }
    })();
    return () => { cancelled = true; };
  }, [selected?.id, form.date, mode]); // eslint-disable-line

  const isSlotTaken = (slot) => daySlots.some(booked => slotsOverlap(booked, slot));

  // If the currently-picked slot becomes unavailable (e.g. date changed), clear it.
  useEffect(() => {
    if (form.timeSlot && form.timeSlot !== "custom" && isSlotTaken(form.timeSlot)) {
      setForm(p => ({ ...p, timeSlot: "" }));
      setAvail(null);
    }
  }, [daySlots]); // eslint-disable-line

  // Multi-day preview
  const updateMultiPreview = (date, endDate) => {
    if (!date || !endDate) { setMultiPreview(null); return; }
    const sd = new Date(date), ed = new Date(endDate);
    if (ed < sd) { setMultiPreview({ error: "End date is before start date." }); return; }
    const bookable = [], skipped = [], cur = new Date(sd);
    while (cur <= ed) {
      if (cur.getDay() === 0) skipped.push(new Date(cur));
      else bookable.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
      if (bookable.length + skipped.length > 60) break;
    }
    if (bookable.length > 14) {
      setMultiPreview({ error: `Range too long — max 14 days, you selected ${bookable.length}.` }); return;
    }
    setMultiPreview({ bookable: bookable.length, skipped: skipped.length });
  };

  const checkAvail = async () => {
    if (!selected) { toast.error("Select a hall first"); return; }
    if ((selected.status || "active") !== "active") {
      setAvail({ available: false, inactive: true });
      toast.error(`${selected.name} is currently ${selected.status === "maintenance" ? "under maintenance" : "inactive"}`);
      return;
    }
    const slot = form.timeSlot === "custom" ? form.customSlot : form.timeSlot;
    if (!form.date || !slot) { toast.error("Select a date and time slot"); return; }
    setChecking(true); setAvail(null);
    try {
      let r;
      if (mode === "multi" && form.endDate) {
        r = await api.checkAvailRange(selected.id, form.date, form.endDate, slot);
      } else {
        r = await api.checkAvail(selected.id, form.date, slot);
      }
      setAvail(r);
      if (r.available) toast.success("Slot is available!");
      else toast.error("Not available — try another time or date");
    } catch(e) { toast.error(e.message); }
    finally { setChecking(false); }
  };

  const submit = async () => {
    if (!avail?.available) { toast.error("Check availability first"); return; }
    const slot = form.timeSlot === "custom" ? form.customSlot : form.timeSlot;
    if (!form.eventName || !form.name) { toast.error("Event name and coordinator name are required"); return; }
    setSubmitting(true);
    try {
      const payload = {
        // The Worker validates on `hall` (same field the availability check uses).
        // Keep hallId/hallNumber/hallName too for downstream display + compatibility.
        hall: selected.id,
        hallId: selected.id, hallNumber: selected.number, hallName: selected.name,
        date: form.date, endDate: mode === "multi" ? form.endDate : undefined,
        timeSlot: slot, bookingType: mode,
        eventName: form.eventName, deptClub: form.deptClub,
        name: form.name, phone: form.phone, email: form.email,
        specialReqs: form.specialReqs,
      };
      const r = await api.addBooking(payload);

      // Upload letter if selected
      let uploadNote = "";
      if (letterRef.current?.files?.[0]) {
        try {
          const file = letterRef.current.files[0];
          const b64  = await fileToBase64(file);
          const ul   = await api.uploadLetter(r.bookingID, file.name, file.type, b64);
          uploadNote = ul.success ? "✓ Permission letter uploaded." : "⚠ Letter upload failed — you can upload later.";
        } catch { uploadNote = "⚠ Letter upload failed — you can upload later."; }
      }

      setResult({ ...r, hallName: selected.name, slot, eventName: form.eventName, name: form.name, uploadNote, isMultiDay: mode === "multi" });
      toast.success(`Booking submitted! ID: ${r.bookingID}`);
    } catch(e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  // Download acknowledgment as text (plain HTML approach — no PDF lib needed)
  const downloadAck = () => {
    if (!result) return;
    const content = `BMSIT&M — Booking Acknowledgment\n\nBooking ID  : ${result.bookingID}\nEvent       : ${result.eventName}\nAuditorium  : ${result.hallName}\nDate        : ${form.date}${mode === "multi" ? " to " + form.endDate : ""}\nTime slot   : ${result.slot}\nCoordinator : ${result.name}\nStatus      : Pending (awaiting hall incharge approval)\n\nSubmitted at: ${new Date().toLocaleString("en-IN")}\n\nNote: A confirmation email has been sent. This acknowledgment is for your records.`;
    const blob = new Blob([content], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `ACK_${result.bookingID}.txt`; a.click();
  };

  const hallList = halls.data || [];

  if (result) return (
    <Shell role="portal" title="Booking submitted">
      <div className="max-w-lg mx-auto space-y-4">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="glass rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/12 border border-emerald-500/20 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h2 className="font-bold">Booking submitted{result.isMultiDay ? ` — ${result.totalDays || ""} day series` : ""}</h2>
              <p className="text-xs text-muted-foreground">Pending hall incharge approval</p>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            {[["Booking ID", result.bookingID], ["Hall", result.hallName], ["Event", result.eventName], ["Date", form.date + (mode === "multi" ? " → " + form.endDate : "")], ["Time slot", result.slot]].map(([k, v]) => (
              <div key={k} className="flex justify-between py-1 border-b border-border/30">
                <span className="text-muted-foreground">{k}</span>
                <span className="font-medium font-mono text-xs">{v}</span>
              </div>
            ))}
          </div>
          {result.uploadNote && <p className="text-xs text-muted-foreground">{result.uploadNote}</p>}
          <div className="flex gap-3 flex-wrap">
            <Button onClick={downloadAck} variant="outline" className="gap-2 flex-1">
              <Download className="h-4 w-4" />Download acknowledgment
            </Button>
            <Button onClick={() => nav("/portal/events?bid=" + result.bookingID)} className="gap-2 flex-1">
              Publish as event →
            </Button>
          </div>
          <Button variant="ghost" onClick={() => { setResult(null); setAvail(null); setForm({ date:"",endDate:"",timeSlot:"",customSlot:"",eventName:"",deptClub:"",name:"",phone:"",email:"",specialReqs:"",bookingType:"single" }); }} className="w-full text-xs text-muted-foreground">
            Book another hall
          </Button>
        </motion.div>
      </div>
    </Shell>
  );

  return (
    <Shell role="portal" title="Book a hall" subtitle="Reserve BSN Auditorium, APJ Hall, Seminar halls and more">
      <div className="max-w-2xl space-y-5">
        {/* Hall picker */}
        <div className="glass rounded-2xl p-5">
          <h3 className="font-bold text-[15px] mb-4">Select hall</h3>
          {halls.loading ? (
            <div className="grid grid-cols-2 gap-3">{[...Array(4)].map((_, i) => <div key={i} className="h-20 rounded-xl shimmer" />)}</div>
          ) : hallList.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No halls configured yet. Contact the super admin to add auditoriums.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {hallList.map((h, i) => {
                const inactive = (h.status || "active") !== "active";
                return (
                <button key={h.id} onClick={() => { setSelected(h); setAvail(null); }}
                  className={cn("text-left p-4 rounded-2xl border-1.5 transition-all duration-150 card-tap relative overflow-hidden",
                    selected?.id === h.id
                      ? (inactive ? "border-amber-500 bg-amber-50/60 dark:bg-amber-950/20" : "border-blue-500 bg-blue-50/60 dark:bg-blue-950/30 shadow-sm shadow-blue-500/10")
                      : "border-border bg-card hover:border-blue-300 dark:hover:border-blue-700/60 hover:shadow-sm",
                    inactive && "opacity-90")}
                >
                  {selected?.id === h.id && !inactive && (
                    <div className="absolute top-2.5 right-2.5 w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center">
                      <CheckCircle2 className="h-3 w-3 text-white" />
                    </div>
                  )}
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center mb-2.5" style={{ background: `${HALL_COLORS[i % 6]}16` }}>
                    <Building2 className="h-4 w-4" style={{ color: HALL_COLORS[i % 6] }} />
                  </div>
                  <div className="font-semibold text-sm leading-tight text-foreground">{h.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{h.capacity} seats</div>
                  {inactive && (
                    <span className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 dark:text-amber-400 bg-amber-500/15 px-1.5 py-0.5 rounded-full">
                      <AlertCircle className="h-3 w-3" />{h.status === "maintenance" ? "Under maintenance" : "Inactive"}
                    </span>
                  )}
                </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Booking mode + date */}
        <AnimatePresence>
          {selected && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-5 space-y-4">
              <h3 className="font-bold text-[15px]">Date & time — {selected.name}</h3>

              {(selected.status || "active") !== "active" && (
                <div className="flex items-start gap-2 text-sm p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300 border border-amber-500/30">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>
                    <strong>{selected.name} is currently {selected.status === "maintenance" ? "under maintenance" : "inactive"}.</strong>{" "}
                    {selected.maintenanceNote || "Bookings for this auditorium are disabled. Please choose another hall or check back later."}
                  </span>
                </div>
              )}

              {/* Single/multi toggle */}
              <div className="flex gap-1 p-1 rounded-xl bg-muted">
                {[["single","Single day"],["multi","Multi-day"]].map(([v, l]) => (
                  <button key={v} onClick={() => { setMode(v); setAvail(null); setMultiPreview(null); }}
                    className={cn("flex-1 py-2 rounded-lg text-xs font-semibold transition-all duration-150",
                      mode === v ? "bg-card text-blue-600 shadow-sm" : "text-muted-foreground hover:text-foreground")}
                  >{l}</button>
                ))}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>{mode === "multi" ? "Start date" : "Date"}</Label>
                  <Input type="date" value={form.date} min={new Date().toISOString().slice(0,10)}
                    onChange={e => { f("date")(e); if (mode === "multi") updateMultiPreview(e.target.value, form.endDate); }} />
                </div>
                {mode === "multi" && (
                  <div className="space-y-1.5">
                    <Label>End date</Label>
                    <Input type="date" value={form.endDate} min={form.date}
                      onChange={e => { f("endDate")(e); updateMultiPreview(form.date, e.target.value); }} />
                  </div>
                )}
              </div>

              {/* Multi-day preview */}
              {multiPreview && (
                <div className={cn("text-xs p-3 rounded-xl", multiPreview.error ? "bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400" : "bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400")}>
                  {multiPreview.error || `${multiPreview.bookable} bookable day${multiPreview.bookable !== 1 ? "s" : ""}${multiPreview.skipped > 0 ? ` · ${multiPreview.skipped} Sunday${multiPreview.skipped !== 1 ? "s" : ""} skipped` : ""}`}
                </div>
              )}

              {/* Time slot */}
              <div className="space-y-1.5">
                <Label>Time slot</Label>
                <select
                  value={form.timeSlot}
                  onChange={f("timeSlot")}
                  className="flex h-10 w-full rounded-xl border border-border bg-background px-4 text-sm focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-500/15 transition-all hover:border-blue-300 dark:hover:border-blue-700/60"
                >
                  <option value="">Select a slot…</option>
                  {TIME_SLOTS.map(s => {
                    const taken = mode !== "multi" && isSlotTaken(s);
                    return <option key={s} value={s} disabled={taken}>{s}{taken ? " — already booked" : ""}</option>;
                  })}
                  <option value="custom">Custom…</option>
                </select>
                {mode !== "multi" && daySlots.length > 0 && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400 flex items-start gap-1.5 pt-0.5">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-px" />
                    <span>Already booked on this date for {selected.name}: {daySlots.join(", ")}. Overlapping slots are disabled — pick a free slot or another hall.</span>
                  </p>
                )}
                {form.timeSlot === "custom" && (
                  <Input value={form.customSlot} onChange={f("customSlot")} placeholder="e.g. 10:30 AM – 2:00 PM" className="mt-2" />
                )}
              </div>

              <Button onClick={checkAvail} disabled={checking || (selected.status || "active") !== "active" || !form.date || !(form.timeSlot && (form.timeSlot !== "custom" || form.customSlot))} className="w-full gap-2">
                {checking ? "Checking…" : "Check availability"} <ChevronRight className="h-4 w-4" />
              </Button>

              {avail && (
                <div className={cn("flex items-center gap-2 text-sm p-3 rounded-xl", avail.available ? "bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400" : "bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400")}>
                  {avail.available ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertCircle className="h-4 w-4 shrink-0" />}
                  {avail.available ? "Available — fill in the details below" : (avail.conflictInfo || "Not available for this slot")}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Booking details */}
        <AnimatePresence>
          {avail?.available && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-5 space-y-4">
              <h3 className="font-bold text-[15px]">Booking details</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5 sm:col-span-2"><Label>Event / programme name *</Label>
                  <Input value={form.eventName} onChange={f("eventName")} placeholder="e.g. National Workshop on AI & ML" />
                </div>
                <div className="space-y-1.5"><Label>Department / Club</Label>
                  <Input value={form.deptClub} onChange={f("deptClub")} placeholder="CSE Dept / ACM Club" />
                </div>
                <div className="space-y-1.5"><Label>Coordinator name *</Label>
                  <Input value={form.name} onChange={f("name")} placeholder="Dr. / Prof. name" />
                </div>
                <div className="space-y-1.5"><Label>Phone</Label>
                  <Input type="tel" value={form.phone} onChange={f("phone")} placeholder="+91 9XXXXXXXXX" />
                </div>
                <div className="space-y-1.5"><Label>Email</Label>
                  <Input type="email" value={form.email} onChange={f("email")} placeholder="coordinator@bmsit.in" />
                </div>
                <div className="space-y-1.5 sm:col-span-2"><Label>Special requirements</Label>
                  <Textarea rows={2} value={form.specialReqs} onChange={f("specialReqs")} placeholder="Projector, PA system, podium, live streaming…" />
                </div>
              </div>

              {/* Permission letter upload */}
              <div className="space-y-1.5">
                <Label>Permission letter <span className="text-muted-foreground font-normal">(optional — PDF, JPG, PNG, max 10 MB)</span></Label>
                <input ref={letterRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
                  className="text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-blue-600/10 file:text-blue-600 dark:file:text-blue-400 hover:file:bg-blue-600/20 cursor-pointer" />
              </div>

              <Button onClick={submit} disabled={submitting || !form.eventName || !form.name} className="w-full gap-2">
                {submitting ? "Submitting…" : "Submit booking request"}
              </Button>
              <p className="text-[11px] text-muted-foreground text-center">
                Your booking will be reviewed by the hall incharge. You'll receive an email confirmation.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Shell>
  );
}
